import { AboutUsHero } from "~/components/about-us/AboutUsHero";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./about-us.module.css";
import { HoverableImageLink } from "~/components/about-us/HoverableImageLink";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { Link } from "~/components/RouterLink";

import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";

type Localization = {
  heading: string;
  subheading1: string;
  subheading2PracticeName: string;
  subheading2Remainder: string;
  meetTheDoctor: string;
  officeTour: string;
  careers: string;
  meetTheTeam: string;
  invisalignMiami: string;
};

const englishLocalization: Localization = {
  heading: "We're Here to Make You Smile",
  subheading1: "Transform your smile while enjoying yourself every minute.",
  subheading2PracticeName: "Miami Orthodontic Specialists",
  subheading2Remainder:
    " are dedicated to giving you personalized care using the latest in orthodontic technologies and treatments.",
  meetTheDoctor: "Meet The Doctors",
  officeTour: "Office Tour",
  careers: "Careers",
  meetTheTeam: "Meet the Team",
  invisalignMiami: "Invisalign in Miami",
};

const spanishLocalization: Localization = {
  heading: "Estamos Aquí Para Hacerte Sonreír",
  subheading1: "Transforma tu sonrisa mientras disfrutas cada minuto.",
  subheading2PracticeName: "Los especialistas en ortodoncia de Miami",
  subheading2Remainder:
    " se dedica a brindarte atención personalizada utilizando lo último en tecnologías y tratamientos ortodónticos.",
  meetTheDoctor: "Conozca al Doctor",
  officeTour: "Recorrido por la Oficina",
  careers: "Oportunidades Laborales",
  meetTheTeam: "Conozca al Equipo",
  invisalignMiami: "Invisalign en Miami",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export default function AboutUsPage() {
  const getLocalization = useLocalization(localized);
  return (
    <>
      <AboutUsHero />
      <Flex direction="column" align="center" width="100dvw">
        <Flex
          width="100%"
          direction="column"
          justify="center"
          align="center"
          className={styles.aboutUsContent}
        >
          <Flex
            width="100%"
            direction="column"
            justify="center"
            align="center"
            gap="5"
            maxWidth="720px"
          >
            <Heading as="h2" className={styles.aboutUsHeading}>
              {getLocalization("heading")}
            </Heading>
            <Text size="5">{getLocalization("subheading1")} </Text>
            <Text size="5">
              <Link to="/miami-orthodontics" className={styles.inlineLink}>
                {getLocalization("subheading2PracticeName")}
              </Link>
              {getLocalization("subheading2Remainder")}{" "}
            </Text>
          </Flex>
        </Flex>
        <Flex width="100%" justify="center" className={styles.imageLinkSection}>
          <Flex
            maxWidth="1060px"
            wrap="wrap"
            width="100%"
            gap="4"
            justify={{ initial: "center", md: "between" }}
            direction={{ initial: "column", md: "row" }}
          >
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/about-dr.jpg"
                label={getLocalization("meetTheDoctor")}
                to="/meet-our-doctors"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/miami-ortho-3.jpg"
                label={getLocalization("officeTour")}
                to="/office-tour"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/careers-1.jpg"
                label={getLocalization("careers")}
                backgroundPosition="50% 90%"
                to="/careers"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/meet-the-team-header-2026.jpg"
                label={getLocalization("meetTheTeam")}
                backgroundPosition="50% 30%"
                to="/meet-the-team"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "100%" }}>
              <HoverableImageLink
                imageUrl="/why-choose-us-2.jpg"
                label={getLocalization("invisalignMiami")}
                backgroundPosition="50% 40%"
                to="/invisalign-miami"
              />
            </Box>
          </Flex>
        </Flex>
        <Box className={styles.aboutUsForm}>
          <HomeConsultForm />
        </Box>
      </Flex>
    </>
  );
}
